import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Send, FileText, Building2, User, MapPin, Landmark, Upload } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PravaahLogo } from "@/components/PravaahLogo";

// Public, unauthenticated dealer / client application form. Submissions land
// in the admin Onboarding queue as PENDING.
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DOC_TYPES = [
  { key: "pan",              label: "PAN Card" },
  { key: "gst",              label: "GST Certificate" },
  { key: "aadhar",           label: "Aadhar Card" },
  { key: "cancelled_cheque", label: "Cancelled Cheque" },
  { key: "letterhead",       label: "Firm Letterhead" },
  { key: "profile_photo",    label: "Profile Photo" },
  { key: "shop_photo",       label: "Shop Photo" },
];

export default function PublicBusinessAssociateForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [uploading, setUploading] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [form, setForm] = useState({
    firm_name: "", contact_person: "", mobile_number: "", email: "",
    party_type: "DEALER", entity_type: "", gstin: "", pan: "",
    address: "", state: "", district: "", taluka: "", region_id: "GJ",
    bank_name: "", bank_account_number: "", ifsc_code: "", bank_branch: "",
    notes: "", declaration_accepted: false,
  });
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.declaration_accepted) {
      toast.error("Please accept the declaration to submit");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/business-associate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setSubmission(data.data);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!submission) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be ≤ 10MB");
      return;
    }
    setUploading((s) => ({ ...s, [docType]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `${API}/public/business-associate/${submission.party_id}/document?submission_token=${submission.submission_token}&doc_type=${docType}`,
        { method: "POST", body: fd },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setUploaded((s) => ({ ...s, [docType]: file.name }));
      toast.success(`${docType} uploaded`);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading((s) => ({ ...s, [docType]: false }));
    }
  };

  if (submission) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8" data-testid="ba-form-success">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold font-['Sora'] text-slate-900">Application Received</h1>
            <p className="text-slate-500 text-sm mt-1">Reference ID <span className="font-mono font-semibold text-slate-900">{submission.party_id}</span></p>
            <p className="text-slate-600 mt-3 max-w-md">{submission.message}</p>
          </div>
          <div className="border-t border-slate-200 pt-6">
            <h2 className="font-semibold text-slate-900 mb-1">Upload supporting documents (optional)</h2>
            <p className="text-xs text-slate-500 mb-4">Upload up to 10 MB per file. Token valid for 30 minutes.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DOC_TYPES.map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition ${
                    uploaded[key]
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-200 hover:border-[#8B1A1A] hover:bg-slate-50"
                  }`}
                >
                  <Upload className={`w-4 h-4 ${uploaded[key] ? "text-emerald-600" : "text-slate-400"}`} />
                  <span className={`flex-1 text-sm ${uploaded[key] ? "text-emerald-800" : "text-slate-700"}`}>{label}</span>
                  {uploading[key] && <span className="text-xs text-slate-400">Uploading…</span>}
                  {uploaded[key] && <span className="text-xs text-emerald-600 font-medium">✓</span>}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    disabled={!!uploading[key]}
                    onChange={(e) => e.target.files?.[0] && handleFileUpload(key, e.target.files[0])}
                    data-testid={`ba-upload-${key}`}
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <Button onClick={() => navigate("/login")} className="btn-primary" data-testid="ba-form-done">
                Done
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top brand bar */}
      <div className="bg-[#0F172A] py-4 px-6 flex items-center justify-between">
        <PravaahLogo variant="full" theme="light" size={36} dataTestId="ba-form-logo" />
        <Button variant="ghost" onClick={() => navigate("/login")} className="text-white hover:bg-white/10" data-testid="ba-form-login-link">
          Already a partner? Login
        </Button>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold font-['Sora'] text-slate-900">Become a Pravaah Partner</h1>
          <p className="text-slate-500 mt-2">Fill in your business details to apply as a dealer or client. Our team reviews every application within 2 business days.</p>
        </div>

        <form onSubmit={submitForm} className="space-y-6" data-testid="ba-form">
          <Section title="Business Identity" icon={Building2}>
            <Field label="Firm Name *" value={form.firm_name} onChange={(v) => setField("firm_name", v)} required testid="ba-firm-name" />
            <Field label="Party Type *" type="select" value={form.party_type} onChange={(v) => setField("party_type", v)}
              options={[{v: "DEALER", l: "Dealer"}, {v: "DISTRIBUTOR", l: "Distributor"}, {v: "RETAILER", l: "Retailer"}, {v: "CLIENT", l: "Client / Project"}]} testid="ba-party-type" />
            <Field label="Entity Type" type="select" value={form.entity_type} onChange={(v) => setField("entity_type", v)}
              options={[{v: "PROPRIETORSHIP", l: "Proprietorship"}, {v: "PARTNERSHIP", l: "Partnership"}, {v: "PRIVATE_LIMITED", l: "Private Limited"}, {v: "LLP", l: "LLP"}, {v: "OTHER", l: "Other"}]} testid="ba-entity-type" />
          </Section>

          <Section title="Contact" icon={User}>
            <Field label="Contact Person *" value={form.contact_person} onChange={(v) => setField("contact_person", v)} required testid="ba-contact-person" />
            <Field label="Mobile *" value={form.mobile_number} onChange={(v) => setField("mobile_number", v)} required testid="ba-mobile" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setField("email", v)} testid="ba-email" />
          </Section>

          <Section title="Address & Region" icon={MapPin}>
            <Field label="Region *" type="select" value={form.region_id} onChange={(v) => setField("region_id", v)}
              options={[{v: "GJ", l: "Gujarat"}, {v: "RJ", l: "Rajasthan"}]} testid="ba-region" />
            <Field label="State" value={form.state} onChange={(v) => setField("state", v)} testid="ba-state" />
            <Field label="District" value={form.district} onChange={(v) => setField("district", v)} testid="ba-district" />
            <Field label="Taluka" value={form.taluka} onChange={(v) => setField("taluka", v)} testid="ba-taluka" />
            <Field label="Address" value={form.address} onChange={(v) => setField("address", v)} full testid="ba-address" />
          </Section>

          <Section title="Tax Details" icon={FileText}>
            <Field label="GSTIN" value={form.gstin} onChange={(v) => setField("gstin", v.toUpperCase())} testid="ba-gstin" mono />
            <Field label="PAN" value={form.pan} onChange={(v) => setField("pan", v.toUpperCase())} testid="ba-pan" mono />
          </Section>

          <Section title="Bank Details" icon={Landmark}>
            <Field label="Bank Name" value={form.bank_name} onChange={(v) => setField("bank_name", v)} testid="ba-bank-name" />
            <Field label="Account Number" value={form.bank_account_number} onChange={(v) => setField("bank_account_number", v)} mono testid="ba-bank-account" />
            <Field label="IFSC" value={form.ifsc_code} onChange={(v) => setField("ifsc_code", v.toUpperCase())} mono testid="ba-ifsc" />
            <Field label="Branch" value={form.bank_branch} onChange={(v) => setField("bank_branch", v)} testid="ba-branch" />
          </Section>

          <Section title="Anything else?" icon={FileText}>
            <Field label="Notes" type="textarea" value={form.notes} onChange={(v) => setField("notes", v)} full testid="ba-notes" />
          </Section>

          <Card className="p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.declaration_accepted}
                onChange={(e) => setField("declaration_accepted", e.target.checked)}
                className="mt-1"
                data-testid="ba-declaration"
              />
              <span className="text-sm text-slate-700">
                I declare that the information provided is true and accurate to the best of my knowledge.
                I understand that Pravaah may verify these details and reach out via the contact information above.
              </span>
            </label>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} className="btn-primary px-8 py-6 text-base" data-testid="ba-submit">
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Submitting…" : "Submit Application"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <Card className="p-5">
    <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
      <Icon className="w-5 h-5 text-[#8B1A1A]" />
      <h2 className="font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </Card>
);

const Field = ({ label, value, onChange, required, type = "text", options, full, mono, testid }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</Label>
    {type === "select" ? (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    ) : type === "textarea" ? (
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full min-h-[80px] rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1A1A]/30"
        data-testid={testid}
      />
    ) : (
      <Input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className={mono ? "font-mono" : ""} data-testid={testid}
      />
    )}
  </div>
);
